import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createTranslator, type TranslateFn, type TranslationKey } from '@astral/localization';
import { directionFor, type Language, type TextDirection } from '@astral/shared';

interface I18nContextValue {
  language: Language;
  dir: TextDirection;
  t: TranslateFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  language: Language;
  children: ReactNode;
}

/**
 * Provides the active translator down the tree. The language is controlled by
 * the caller (sourced from the authenticated user's saved preference), so
 * changing it re-renders every consumer with new strings.
 */
export function I18nProvider({ language, children }: I18nProviderProps): JSX.Element {
  const value = useMemo<I18nContextValue>(
    () => ({ language, dir: directionFor(language), t: createTranslator(language) }),
    [language],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Access the translator and language metadata. */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider');
  return ctx;
}

/** Shorthand hook returning just the translate function. */
export function useT(): TranslateFn {
  return useI18n().t;
}

export type { TranslationKey };
